import axios from "axios";
import { getCookie, getUserAuthToken, clearAuthCookies } from "../utils/cookieUtils";
import Swal from "sweetalert2";

// API Base URLs — configure via front-end/.env / .env.development (REACT_APP_*)
const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;
const API_BASE_PROFILE_URL = `${process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:8000'}/api`;
// Admin and user share the DB; files live on admin server storage
const ADMIN_STORAGE_BASE_URL = (process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:8000').replace(/\/$/, '');

if (process.env.NODE_ENV === 'development') {
    // Helps confirm local npm start is not still pointing at dig/live
    // eslint-disable-next-line no-console
    console.info('[user-api] REACT_APP_API_URL =>', process.env.REACT_APP_API_URL || '(fallback localhost:8000)');
}

/** Rewrite storage URLs to admin server (profile images, post attachments, inline images) */
export const resolveAdminStorageUrl = (url) => {
    if (!url) return null;

    const value = String(url).trim();
    const storageMatch = value.match(/^(?:https?:\/\/[^/]+)?(\/storage\/.+)$/);

    if (storageMatch) {
        return `${ADMIN_STORAGE_BASE_URL}${storageMatch[1].split('?')[0]}`;
    }

    const relative = value.replace(/^\/+/, '');
    if (relative.startsWith('storage/')) {
        return `${ADMIN_STORAGE_BASE_URL}/${relative.split('?')[0]}`;
    }
    if (/^(profile_images|faq_attachments|attachments)\//.test(relative)) {
        return `${ADMIN_STORAGE_BASE_URL}/storage/${relative.split('?')[0]}`;
    }
    if (relative.startsWith('public/')) {
        return `${ADMIN_STORAGE_BASE_URL}/${relative.replace(/^public/, 'storage').split('?')[0]}`;
    }

    return value;
};

export const resolveProfileImageUrl = (profileImage) => resolveAdminStorageUrl(profileImage);

const resolveStorageUrlsInHtml = (html) => {
    if (!html || typeof html !== 'string') return html;

    return html.replace(/(src|href)=(["'])([^"']+)\2/gi, (match, attr, quote, url) => {
        const resolved = resolveAdminStorageUrl(url);
        return `${attr}=${quote}${resolved}${quote}`;
    });
};

const withProfileImageUrl = (user) => {
    if (!user) return user;
    return {
        ...user,
        profile_image: resolveProfileImageUrl(user.profile_image),
    };
};

const withAdminStorageUrls = (data) => {
    if (!data) return data;

    if (data.user) {
        data.user = withProfileImageUrl(data.user);
    }

    if (Array.isArray(data.faqs)) {
        data.faqs = data.faqs.map((faq) => ({
            ...faq,
            content: resolveStorageUrlsInHtml(faq.content),
            attachments: Array.isArray(faq.attachments)
                ? faq.attachments.map((file) => ({
                    ...file,
                    url: resolveAdminStorageUrl(file.url || file.file_path),
                }))
                : faq.attachments,
        }));
    }

    return data;
};

// Create axios instance with credentials for cookie-based authentication
const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true, // Important: sends cookies with requests
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
});

// Request interceptor for adding auth token
api.interceptors.request.use(
    (config) => {
        // ✅ Mark verify-mobile-login requests to skip error popup
        if (config.url && config.url.includes('verify-mobile-login')) {
            config._skipErrorPopup = true;
        }
        
        // ✅ Remove Content-Type header for FormData - axios will set it automatically with boundary
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        
        // Add auth token if available (from cookie)
        const token = getUserAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add company_id to all requests (from cookie)
        const companyId = getCookie('company_id');
        if (companyId && 
            companyId !== 'undefined' && 
            companyId !== 'null' && 
            companyId.trim() !== '' &&
            !isNaN(companyId)) {
            // Add as query parameter for GET requests
            if (config.method === 'get' || config.method === 'GET') {
                config.params = config.params || {};
                config.params.company_id = companyId;
            } 
            // Add to body for POST/PUT/DELETE requests
            else {
                if (config.data instanceof FormData) {
                    config.data.append('company_id', companyId);
                } else if (typeof config.data === 'object' && config.data !== null) {
                    config.data.company_id = companyId;
                }
            }
            // Also add as header (for backend to check)
            config.headers['X-Company-Id'] = companyId;
        }
        
        // Add user_id to all requests (from cookie)
        const userId = getCookie('user_id');
        if (userId && 
            userId !== 'undefined' &&
            userId !== 'null' && 
            userId.trim() !== '') {
            // Add as header for all requests
            config.headers['X-User-Id'] = userId;
            // Also add to body for POST/PUT requests
            if ((config.method === 'post' || config.method === 'POST' || config.method === 'put' || config.method === 'PUT') && 
                typeof config.data === 'object' && config.data !== null && !(config.data instanceof FormData)) {
                config.data.user_id = userId;
            }
        }
        
        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for centralized error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const { status, data } = error.response || {};
        
        if (status === 401) {
            // Unauthorized - clear USER auth only (do not wipe admin cookies on localhost)
            // Skip bounce while switch URL params are still being applied
            const switchParams = new URLSearchParams(window.location.search);
            if (switchParams.get('user_id') && switchParams.get('token')) {
                console.warn('401 during account switch bootstrap — token may not be ready yet');
                return Promise.reject(error);
            }
            clearAuthCookies();
            // Only redirect if not already on login page
            if (window.location.pathname !== '/' && window.location.pathname !== '/verify-mobile') {
                window.location.href = "/";
            }
        } else if (status === 403) {
            // Access forbidden
            console.error('Access forbidden:', data?.message || 'You do not have permission to perform this action.');
            
            // ✅ Skip showing popup for verify-mobile-login endpoint - VerifyMobile.js handles it
            // Check for custom flag first (most reliable)
            if (error.config?._skipErrorPopup === true) {
                console.log('Skipping 403 popup - custom flag set');
                return Promise.reject(error);
            }
            
            // Also check URL as fallback
            const requestUrl = error.config?.url || '';
            if (requestUrl.includes('verify-mobile-login')) {
                console.log('Skipping 403 popup for verify-mobile-login - handled by VerifyMobile.js');
                return Promise.reject(error);
            }
            
            // Show user-friendly error message for other 403 errors
            const errorMessage = data?.message || 'Access forbidden. You do not have permission to perform this action.';
           // alert(errorMessage); // You can replace with a toast/notification system if available
           Swal.fire({
            icon: 'error',
            title: 'Access forbidden',
            text: errorMessage,
           });
        } else if (status === 429) {
            // Rate limit exceeded
            const retryAfter = error.response.headers['retry-after'] || error.response.headers['Retry-After'];
            let errorMessage = 'Too many requests. Please slow down and try again.';
            
            if (retryAfter) {
                const seconds = parseInt(retryAfter);
                const minutes = Math.ceil(seconds / 60);
                errorMessage = `Rate limit exceeded. Please wait ${minutes} minute(s) before trying again.`;
            } else {
                errorMessage = data?.message || 'Too many requests. Please wait a moment and try again.';
            }
            
            console.warn('Rate limit exceeded:', errorMessage);
            //alert(errorMessage); // You can replace with a toast/notification system if available
            Swal.fire({
                icon: 'error',
                title: 'Rate limit exceeded',
                text: errorMessage,
            });
        }
        
        return Promise.reject(error);
    }
);

// Profile API endpoint
const USERS_API_URL = `${API_BASE_URL}/users`; // Users API endpoint

const ADMIN_LOGIN_URL = `${API_BASE_URL}/verify-admin-login`;

// Note: checkAuth endpoint removed - using client-side cookie check only
// Future: Can be re-enabled when backend cookie management is implemented

// Function to get FAQs and Users
export const fetchFAQsAndUsers = () => {
    return api.get('/faqs');
};

// Function to get Users only
export const fetchUsers = () => {
    return api.get('/users');
};

export const fetchUser = async (userId) => {
    const response = await api.get(`/user/${userId}`);
    response.data = withProfileImageUrl(response.data);
    return response;
};

// Update a user by ID
export const updateUser = async (userId, userData) => {
    const response = await api.post(`/update-user/${userId}`, userData);
    if (response.data?.user) {
        response.data.user = withProfileImageUrl(response.data.user);
    }
    return response;
};

// Fetch user data (and optionally their FAQs) with pagination and filtering
export const fetchUserFaqs = async (userId, page = 1, perPage = 25, filters = {}) => {
    const params = { user_id: userId, page, per_page: perPage };
    
    if (filters.status) params.status = filters.status;
    if (filters.category) params.category = filters.category;
    if (filters.search) params.search = filters.search;
    
    const response = await api.get('/user-faqs', { params });
    response.data = withAdminStorageUrls(response.data);
    return response;
};

// Mark a user's FAQ as read
export const markFaqAsRead = (userFaqId) => {
    // ✅ Use relative path since api instance already has baseURL
    return api.post('/mark-as-read', {
        user_faq_id: userFaqId,
    });
};

// Verify mobile login
export const verifyMobileLogin = (userIdentifier) => {
    // ✅ Use relative path since api instance already has baseURL
    // Request interceptor will mark this request to skip error popup
    return api.post('/verify-mobile-login', {
        user: userIdentifier,
    });
};

// Accept FAQ for a specific user
export const acceptFaq = (userFaqId) => {
    // ✅ Use relative path since api instance already has baseURL
    return api.post('/accept-faq', {
        user_faq_id: userFaqId,
    });
};

// Toggle FAQ starred/favourite status for a user
export const updateFavourite = (faqId, userId, isStarred) => {
    // ✅ Use relative path since api instance already has baseURL
    return api.post('/update-favourite', {
        faq_id: faqId,
        user_id: userId,
        is_starred: isStarred,
    });
};

// Get company information (for checking trial status)
export const getCompanyInfo = async () => {
    try {
        const userId = getCookie('user_id');
        const adminId = getCookie('admin_id');
        
        // Add user_id/admin_id as query param (api interceptor will add headers and token automatically)
        const params = {};
        
        if (adminId) {
            params.admin_id = adminId;
        }
        if (userId) {
            params.user_id = userId;
        }
        
      //  console.log('getCompanyInfo - userId:', userId, 'adminId:', adminId);
       // console.log('getCompanyInfo - params:', JSON.stringify(params));
        
        // ✅ Use api instance instead of axios to get automatic token injection
        const { data } = await api.get('/company-info', {
            params
        });
        return data;
    } catch (error) {
        console.error('Error fetching company info:', error);
        console.error('Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw error;
    }
};

// Get company information with explicit userId (for switching from admin)
export const getCompanyInfoWithUserId = async (userId, companyId = null) => {
    try {
        // Add user_id and company_id as query params (api interceptor will add headers and token automatically)
        const params = { user_id: userId };
        
        if (companyId) {
            params.company_id = companyId;
        }
        
        console.log('getCompanyInfoWithUserId - userId:', userId, 'companyId:', companyId);
        
        // ✅ Use api instance instead of axios to get automatic token injection
        const { data } = await api.get('/company-info', {
            params
        });
        return data;
    } catch (error) {
        console.error('Error fetching company info with userId:', error);
        console.error('Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw error;
    }
};

// Check welcome tour status
export const checkWelcomeTourStatus = async () => {
    try {
        const userId = getCookie('user_id');
        const adminId = getCookie('admin_id');
      //  console.log('userId testing welcome tour status', userId);
      //  console.log('adminId testing welcome tour status', adminId);
        
        // Add user_id/admin_id as query param (api interceptor will add headers and token automatically)
        const params = {};
        
        if (adminId) {
            params.admin_id = adminId;
        }
        if (userId) {
            params.user_id = userId;
        }
        
       // console.log('✅ Reached point after building params');
      //  console.log('params testing welcome tour status', JSON.stringify(params));
      //  console.log('API URL:', `${API_BASE_URL}/welcome-tour/status`);
      //  console.log('About to make api request...');
        
        // ✅ Use api instance to get automatic token injection
        const { data } = await api.get('/welcome-tour/status', {
            params
        });
        
       // console.log('Welcome tour status response:', data);
        return data;
    } catch (error) {
        console.error('Error checking welcome tour status:', error);
        console.error('Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            config: error.config,
        });
        return { status: false, has_seen_tour: true }; // Default to true on error
    }
};

// Mark welcome tour as completed
export const markWelcomeTourCompleted = async () => {
    try {
        const userId = getCookie('user_id');
        const adminId = getCookie('admin_id');
        
        // Add user_id/admin_id as body data and query param (api interceptor will add headers and token automatically)
        const bodyData = {};
        const params = {};
        
        if (adminId) {
            bodyData.admin_id = adminId;
            params.admin_id = adminId;
        }
        if (userId) {
            bodyData.user_id = userId;
            params.user_id = userId;
        }
        
      //  console.log('Marking welcome tour as completed with:', { bodyData, params });
        
        // ✅ Use api instance to get automatic token injection
        const { data } = await api.post('/welcome-tour/complete', bodyData, {
            params
        });
        
      //  console.log('Welcome tour completion response:', data);
        return data;
    } catch (error) {
        console.error('Error marking welcome tour as completed:', error);
        console.error('Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            config: error.config,
        });
        throw error;
    }
};

// Check if current user has an admin account
export const checkHasAdminAccount = async () => {
    try {
        const { data } = await api.get('/check-has-admin-account');
        return data;
    } catch (error) {
        console.error('Error checking admin account:', error);
        return { has_admin_account: false };
    }
};

// Switch from user to admin account
export const switchToAdminAccount = async () => {
    try {
        const userId = getCookie('user_id');
        // ✅ Use api instance to get automatic token injection
        const { data } = await api.post('/switch-to-admin-account', {
            user_id: userId
        });
        return data;
    } catch (error) {
        console.error('Error switching to admin account:', error);
        throw error;
    }
};

// Note: logoutUser endpoint removed - using client-side cookie removal only
// Future: Can be re-enabled when backend cookie management is implemented

// ============================================================================
// SYSTEM UPDATES API (Read-only for users)
// ============================================================================

/**
 * Get public updates for current company (User view - read-only)
 */
export const getPublicUpdates = async () => {
    try {
        const { data } = await api.get('/updates/public');
        return data;
    } catch (error) {
        console.error('Error fetching public updates:', error);
        throw error;
    }
};

export { API_BASE_URL, API_BASE_PROFILE_URL, ADMIN_STORAGE_BASE_URL };

export default api;

// ============================================================================
// Training modules and reading points for the signed-in employee
// ============================================================================

/** The modules this employee has been given, with progress. */
export const fetchMyModules = () => {
    return api.get('/my/modules');
};

/** The posts inside one of this employee's modules, and which are read. */
export const fetchMyModuleDetail = (moduleId) => {
    return api.get(`/my/modules/${moduleId}`);
};

/** Level, points and the next step - drives the ring on the profile picture. */
export const fetchMyStatus = () => {
    return api.get('/my/status');
};
